import { IsiDevice, PlatformDetails, AppLaunchMode, Log } from "../dependencies";
import { StreamingQuality, StreamingResolution, StreamUpdateEvent } from "../interfaces";
import { StreamingStats } from "../internalinterfaces";
import { ExtendedDebugStats, StaticStreamStats } from "../rinterfaces";
import { GetResolutionString } from "../util/utils";
import { RTCInboundRtpStreamExtraStats } from "./binaryreport";
import { MicState, VideoCodecType } from "../nskinterfaces";

const LOGTAG = "streamstats";

interface PerfOscStats {
    value: number;
    count: number;
    max: number;
}

class RollingAverage {
    prevSum: number = 0;
    currentSum: number = 0;
    prevCount: number = 0;
    currentCount: number = 0;

    public update(sum: number, count: number) {
        if (this.prevSum == 0 && this.prevCount == 0) {
            this.prevSum = sum;
            this.prevCount = count;
            return;
        }

        this.currentCount = count;
        this.currentSum = sum;
    }

    public getRollingAvg() {
        const count = this.currentCount - this.prevCount;
        if (count != 0) {
            return (this.currentSum - this.prevSum) / count;
        }
        return 0;
    }

    public reset() {
        this.prevSum = this.currentSum;
        this.prevCount = this.currentCount;
    }
}

const STATS_OVERLAY_ID = "overlay-543d9968";

export class StreamStatsInternal {
    private overlayElement: HTMLElement;
    private streamingQuality: StreamingQuality;
    private fps: number = 0;
    private avgStreamingRate: number = 0;
    private bwu: number = 4;
    private bweMbps: number = 0;
    private lastInboundVideoRtpTimestamp: number = 0;
    private framesDecoded_: number = 0;
    private framesReceived_: number = 0;
    private framesDropped_: number = 0;
    private avgVideoJitterBufferDelay: number = 0;
    private avgGameFps?: number;
    private rtd: number = 0;
    private packetsLost_: number = 0;
    private streamingResolution: StreamingResolution = { width: 0, height: 0 };
    private interFrameDelayMs: number = 0;
    private videoElement: HTMLVideoElement;
    private mainThreadBlockedDuration: number = 0;
    private dcSendDuration: PerfOscStats = { count: 0, max: 0, value: 0 };
    private getStatsDuration: PerfOscStats = { count: 0, max: 0, value: 0 };
    private decodeTimeAvg: RollingAverage = new RollingAverage();
    private cumulativeAvgDecodeTime: number = 0;
    private framesLost_: number = 0;
    private prevFramesLost_: number = 0;
    private extendedDebugStats: ExtendedDebugStats;
    private staticStreamStats: StaticStreamStats;
    private platformDetails: PlatformDetails;
    private codec: VideoCodecType = VideoCodecType.UNKNOWN;
    private softwareDecodeFallback: boolean = false;
    private decoder: string = "";
    private markers: number = 0;

    constructor(
        videoElement: HTMLVideoElement,
        platformDetails: PlatformDetails,
        staticStreamStats: StaticStreamStats
    ) {
        this.videoElement = videoElement;
        this.platformDetails = platformDetails;
        this.staticStreamStats = staticStreamStats;

        this.streamingQuality = {
            qualityScore: 0,
            bandwidthScore: 0,
            networkLossScore: 0,
            latencyScore: 0
        };
        let overlay = document.getElementById(STATS_OVERLAY_ID);
        if (!overlay) {
            overlay = this.createOverlayNode();
            this.videoElement.insertAdjacentElement("afterend", overlay);
        }
        this.overlayElement = overlay;
        this.extendedDebugStats = {
            isVideoElementPaused: false,
            isAudioElementPaused: false,
            isUserInputEnabled: false,
            isVirtualKeyboardVisible: false,
            micState: MicState.UNINITIALIZED,
            isRsdmmActive: false,
            keyboardLayout: "",
            appLaunchMode: AppLaunchMode.Default,
            inputMode: ""
        };
    }

    public updateAvgStreamingRate(
        currentReport: RTCIceCandidatePairStats,
        prevReport: RTCIceCandidatePairStats
    ) {
        let currentBytesReceived = currentReport.bytesReceived ?? 0;
        let prevBytesReceived = prevReport.bytesReceived ?? 0;

        let t1 = currentReport.timestamp ?? 0;
        let t2 = prevReport.timestamp ?? 0;
        if (t1 == t2) return;

        this.avgStreamingRate = (currentBytesReceived - prevBytesReceived) / ((t1 - t2) * 125);
    }

    public updateBwu(currentBweMbps: number) {
        // Make sure to update the bwu ONLY when avgStreamingRate and currentBweMbps are in sync
        //  avgStreamingRate might not be up to date when currentBweMbps is updated since
        //  currentBweMbps is updated by the server whereas avgStreamingRate is updated by the
        //  client. When the bandwidth drops, this leads to a wrong, not consistent values
        if (currentBweMbps && this.avgStreamingRate <= currentBweMbps) {
            this.bwu = (this.avgStreamingRate / currentBweMbps) * 100;
        }
        this.bweMbps = currentBweMbps;
        return this.bwu;
    }

    public updateInboundVideoRtpStats(stats: RTCInboundRtpStreamExtraStats): void {
        if (stats.frameWidth && stats.frameHeight) {
            this.streamingResolution = { width: stats.frameWidth, height: stats.frameHeight };
        }
        if (stats.framesDecoded) {
            const t1 = this.lastInboundVideoRtpTimestamp;
            const t2 = stats.timestamp;
            if (t1 !== t2) {
                const fps = ((stats.framesDecoded - this.framesDecoded_) / (t2 - t1)) * 1000;
                this.fps = Math.floor(fps);
            }
            this.framesDecoded_ = stats.framesDecoded;

            if (stats.totalDecodeTime) {
                this.decodeTimeAvg.update(stats.totalDecodeTime, stats.framesDecoded);
                this.cumulativeAvgDecodeTime = (stats.totalDecodeTime * 1000) / stats.framesDecoded;
            }
        }
        if (stats.framesReceived) {
            this.framesReceived_ = stats.framesReceived;

            if (stats.totalInterFrameDelay) {
                const interFrameDelayMs = stats.totalInterFrameDelay * 1000;
                this.interFrameDelayMs = interFrameDelayMs / (stats.framesReceived - 1);
            }
        }
        if (stats.framesDropped) {
            this.framesDropped_ = stats.framesDropped;
        }
        if (stats.packetsLost) {
            this.packetsLost_ = stats.packetsLost;
        }
        if (stats.pliCount) {
            this.framesLost_ = stats.pliCount;
        }
        this.lastInboundVideoRtpTimestamp = stats.timestamp;
    }

    public updateQScore(sq: StreamingQuality) {
        this.streamingQuality = sq;
    }

    public updateRtd(rtd: number) {
        this.rtd = rtd;
    }

    public updateJitter(jitter: number) {
        this.avgVideoJitterBufferDelay = jitter;
    }

    public updateAvgGameFps(fps: number) {
        this.avgGameFps = fps;
    }

    public getStreamingStats() {
        let streamingStats: StreamingStats = {
            fps: this.fps,
            cumulativeAvgDecodeTime: this.cumulativeAvgDecodeTime,
            avgDecodeTime: this.decodeTimeAvg.getRollingAvg() * 1000,
            bwe: this.bweMbps,
            bwu: this.bwu,
            width: this.streamingResolution.width,
            height: this.streamingResolution.height
        };
        return streamingStats;
    }

    public printStats() {
        const averageDecodeTime = (this.decodeTimeAvg.getRollingAvg() * 1000).toFixed(2);
        Log.i("{4155ac9}", "{a02a36e}", this.framesDecoded_, averageDecodeTime, this.framesDropped_, this.avgVideoJitterBufferDelay.toFixed(2), this.framesLost_, this.rtd, this.avgStreamingRate.toFixed(2), this.bwu.toFixed(2));
    }

    public makeStreamUpdateEvent(): StreamUpdateEvent {
        const event: StreamUpdateEvent = {
            avgGameFps: this.avgGameFps ?? 0,
            /** Streaming FPS */
            fps: this.fps,
            rtd: this.rtd,
            /** Average decode cost (ms) */
            avgDecodeTime: this.decodeTimeAvg.getRollingAvg() * 1000,
            /** Total frame loss since last update */
            frameLoss: this.framesLost_ - this.prevFramesLost_,
            /** Total packet loss */
            packetLoss: this.packetsLost_,
            /** Available bandwidth (Mbps) */
            totalBandwidth: this.bweMbps,
            /** Utilized bandwidth (%) */
            utilizedBandwidth: this.bwu,
            streamingResolution: this.streamingResolution,
            codec: this.codec
        };
        this.prevFramesLost_ = this.framesLost_;
        return event;
    }

    public drawStatsOnScreen(enableDevStats: boolean) {
        this.refreshText(enableDevStats);
        this.dcSendDuration = { value: 0, count: 0, max: 0 };
        this.getStatsDuration = { value: 0, count: 0, max: 0 };
        this.decodeTimeAvg.reset();
    }

    public refreshText(enableDevStats: boolean) {
        this.overlayElement.innerText = this.getText(enableDevStats);
    }

    public setShown(show: boolean) {
        this.overlayElement.style.display = show ? "block" : "none";
    }

    private createOverlayNode() {
        var overlay = document.createElement("div");
        overlay.id = STATS_OVERLAY_ID;
        overlay.style.display = "none";
        overlay.style.position = "fixed";
        //@todo think of a way to remove dependency on platformDetails and remove it as class member
        if (IsiDevice(this.platformDetails)) {
            overlay.style.top = "env(safe-area-inset-top, 0)";
            overlay.style.left = "max(24px, env(safe-area-inset-left, 0))";
        } else {
            overlay.style.top = "0";
            overlay.style.left = "0";
        }
        overlay.style.padding = "0.5em";
        overlay.style.backgroundColor = "rgba(0,0,0,0.5)";
        overlay.style.zIndex = "300";
        overlay.style.fontSize = "12px";
        overlay.style.fontFamily = "monospace";
        overlay.style.color = "white";
        overlay.style.whiteSpace = "pre";
        overlay.style.lineHeight = "100%";
        overlay.style.pointerEvents = "none";
        return overlay;
    }

    public updateDcSendDuration(time: number) {
        this.dcSendDuration.value += time;
        this.dcSendDuration.count += 1;
        this.dcSendDuration.max = Math.max(time, this.dcSendDuration.max);
    }

    public updateStatsDuration(time: number) {
        this.getStatsDuration.value += time;
        this.getStatsDuration.count += 1;
        this.getStatsDuration.max = Math.max(time, this.getStatsDuration.max);
    }

    public updateMainThreadBlockDuration(duration: number) {
        this.mainThreadBlockedDuration = Math.max(duration, this.mainThreadBlockedDuration);
    }

    public updateExtendedDebugStats(stats: ExtendedDebugStats) {
        this.extendedDebugStats = stats;
    }

    public updateVideoCodec(codec: VideoCodecType) {
        this.codec = codec;
    }

    public updateQosMarkerCount() {
        this.markers++;
    }

    public setSoftwareDecodeFallback(fallback: boolean) {
        this.softwareDecodeFallback = fallback;
    }

    public setDecoderImplementation(decoder: string) {
        this.decoder = decoder;
    }

    public getText(enableDevStats: boolean): string {
        const line1 = `Seat: ${this.staticStreamStats.zoneName} (${this.staticStreamStats.requestedRegion}) / ${this.staticStreamStats.gpuType}\n`;

        let line2 = `Game: CMS ${this.staticStreamStats.appId}`;
        if (this.avgGameFps !== undefined) {
            line2 += ` / fps ${this.avgGameFps.toFixed()}`;
        }
        line2 += `\n`;

        const line3 = `Stream: Current ${GetResolutionString(this.streamingResolution)}@${
            this.fps
        } / Default: ${this.staticStreamStats.streamInfo.width}x${
            this.staticStreamStats.streamInfo.height
        }@${this.staticStreamStats.streamInfo.fps} / Codec ${this.codec}\n`;

        const line4 = `Network: RTD ${this.rtd}ms / FL ${this.framesLost_} / PL ${
            this.packetsLost_
        } / J ${this.avgVideoJitterBufferDelay.toFixed(
            2
        )}ms / Bitrate ${this.avgStreamingRate.toFixed(2)}Mbps / BWU ${this.bwu.toFixed(2)}%\n`;

        const line5 = `QOS: frame ${this.framesDecoded_} / FT ${this.interFrameDelayMs.toFixed(
            2
        )} / D ${(this.decodeTimeAvg.getRollingAvg() * 1000).toFixed(2)} / Q ${Math.floor(
            this.streamingQuality.qualityScore
        )} / Marker ${this.markers}\n`;

        const line6 = `Client: ${this.platformDetails.os} ${
            this.staticStreamStats.clientAppVersion
        } ${this.staticStreamStats.clientLocale} Resolution ${GetResolutionString({
            width: window.innerWidth * window.devicePixelRatio,
            height: window.innerHeight * window.devicePixelRatio
        })}\n`;

        const line7 = `Input ${this.extendedDebugStats.inputMode}\n`;

        let text = line1 + line2 + line3 + line4 + line5 + line6 + line7;

        const internalStatsLine1 = `DC ${getAvg(this.dcSendDuration)}ms (${getMax(
            this.dcSendDuration
        )}ms) / Blocked ${this.mainThreadBlockedDuration}\n`;

        const internalStatsLine2 = `Stats ${getAvg(this.getStatsDuration)}ms (${getMax(
            this.getStatsDuration
        )}ms) / FR ${this.framesReceived_} / FDR ${this.framesDropped_}\n`;

        const internalStatsLine3 = `Latency ${this.streamingQuality.latencyScore.toFixed(
            1
        )} / Network ${this.streamingQuality.networkLossScore.toFixed(
            1
        )} / Bandwidth ${this.streamingQuality.bandwidthScore.toFixed(1)}\n`;

        const internalStatsLine4 = `VP ${boolToString(
            this.extendedDebugStats.isVideoElementPaused
        )} / AP ${boolToString(this.extendedDebugStats.isAudioElementPaused)} / UI ${boolToString(
            this.extendedDebugStats.isUserInputEnabled
        )} / VKB ${boolToString(this.extendedDebugStats.isVirtualKeyboardVisible)} / MS ${
            this.extendedDebugStats.micState
        } / RSDMM ${+this.extendedDebugStats.isRsdmmActive}\n`;

        const internalStatsLine5 = `KBL ${this.extendedDebugStats.keyboardLayout} / ALM ${+this
            .extendedDebugStats.appLaunchMode} / SWD ${boolToString(
            this.softwareDecodeFallback
        )} / ${this.decoder}`;

        // Enable dev stats for only internal clients
        if (enableDevStats) {
            text +=
                internalStatsLine1 +
                internalStatsLine2 +
                internalStatsLine3 +
                internalStatsLine4 +
                internalStatsLine5;
        }

        return text;
    }
}

function getAvg(stats: PerfOscStats): string {
    let avg = 0;
    if (stats.count > 0) {
        avg = stats.value / stats.count;
    }
    return avg.toFixed(2);
}

function getMax(stats: PerfOscStats): string {
    return stats.max.toFixed(2);
}

/**
 * Convert boolean to y/n string representation
 */
function boolToString(x: boolean): string {
    return x ? "y" : "n";
}
